import { useRef } from 'react'
import useLocalStorageState from 'use-local-storage-state'
import { create } from 'zustand'

import { useDeepCompareEffect } from 'src/hooks/useDeepCompareEffect'

import { DEFAULT_PAGE_SIZE } from './pagination.config'

type PersistentState = {
  pageSize: number
}

const defaultInit = (pageSize?: number): PersistentState => ({
  pageSize: pageSize || DEFAULT_PAGE_SIZE,
})

type PageStore = {
  pageData: Record<string, number>
  setPage: (key: string, page: number) => void
}

const usePageStore = create<PageStore>((set) => ({
  pageData: {},
  setPage: (key, page) => {
    set((state) => ({
      pageData: {
        ...state.pageData,
        [key]: page,
      },
    }))
  },
}))

export type UsePaginationHelperOptions = {
  pageSize?: number
  page?: number
  resetPageDep?: unknown
}

/**
 * Simplified pagination hook for non-table components
 * Manages page state in Zustand (session) and pageSize in localStorage
 */
export const usePaginationHelper = (
  key: string,
  options?: UsePaginationHelperOptions
) => {
  const isFirstRenderRef = useRef(true)
  const { pageData, setPage } = usePageStore()
  const page = pageData[key] ?? (options?.page || 1)

  const [{ pageSize }, setState] = useLocalStorageState<PersistentState>(
    `Pagination-${key}`,
    {
      defaultValue: defaultInit(options?.pageSize),
    }
  )

  const handlePageChange = (newPage: number, newPageSize?: number) => {
    if (newPageSize && newPageSize !== pageSize) {
      setState({ pageSize: newPageSize })
    }
    setPage(key, newPage)
  }

  useDeepCompareEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false
      return
    }

    setPage(key, options?.page || 1)
  }, [key, options?.resetPageDep, options?.page])

  return {
    paginationVars: {
      limit: pageSize,
      offset: pageSize * (page - 1),
    },
    handlePageChange,
    state: {
      page,
      pageSize,
    },
  }
}
